/**
 * AddressSearchInput — Buscador de direcciones para modo edición
 *
 * Input con autocompletado vía CartoCiudad. Cuando el usuario selecciona
 * un candidato, se geocodifica y se emite un evento con las coordenadas
 * EPSG:25830 para posicionar el marcador.
 *
 * @module components/map/editing/AddressSearchInput
 * @version 1.0.0
 * @date 2026-02-11
 */

import React, { memo, useState, useCallback, useRef, useEffect } from 'react';
import { Search, Loader2, MapPin, X } from 'lucide-react';
import {
  searchCandidates,
  geocodeAddress,
  type AddressCandidate,
  type AddressGeocodedResult,
} from './addressSearch';

// =============================================================================
// TIPOS
// =============================================================================

export interface AddressSearchInputProps {
  /** Callback cuando se geocodifica una dirección */
  onAddressGeocoded: (result: AddressGeocodedResult) => void;
  /** Municipio para acotar la búsqueda (opcional) */
  municipality?: string;
  /** Placeholder del input */
  placeholder?: string;
  /** Deshabilitado */
  disabled?: boolean;
}

/** Tiempo de debounce para búsqueda (ms) */
const DEBOUNCE_MS = 350;

/** Mínimo de caracteres para buscar */
const MIN_QUERY_LENGTH = 3;

// =============================================================================
// COMPONENTE
// =============================================================================

export const AddressSearchInput = memo<AddressSearchInputProps>(
  function AddressSearchInput({
    onAddressGeocoded,
    municipality,
    placeholder = 'Buscar dirección…',
    disabled = false,
  }) {
    const [query, setQuery] = useState('');
    const [candidates, setCandidates] = useState<AddressCandidate[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [isGeocoding, setIsGeocoding] = useState(false);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const [showDropdown, setShowDropdown] = useState(false);

    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const abortRef = useRef<AbortController | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // --- Limpiar al desmontar ---
    useEffect(() => {
      return () => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        abortRef.current?.abort();
      };
    }, []);

    // --- Búsqueda con debounce ---
    const handleInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setQuery(value);
        setErrorMsg(null);

        // Cancelar búsqueda anterior
        if (debounceRef.current) clearTimeout(debounceRef.current);
        abortRef.current?.abort();

        if (value.trim().length < MIN_QUERY_LENGTH) {
          setCandidates([]);
          setShowDropdown(false);
          return;
        }

        debounceRef.current = setTimeout(async () => {
          setIsSearching(true);
          const controller = new AbortController();
          abortRef.current = controller;

          const result = await searchCandidates(value, {
            signal: controller.signal,
            municipality,
            maxCandidates: 5,
          });

          if (controller.signal.aborted) return;

          setIsSearching(false);
          if (result.ok) {
            setCandidates(result.data);
            setShowDropdown(true);
            setErrorMsg(null);
          } else {
            setCandidates([]);
            setShowDropdown(false);
            if (result.error.type !== 'empty-query') {
              setErrorMsg(result.error.message);
            }
          }
        }, DEBOUNCE_MS);
      },
      [municipality],
    );

    // --- Seleccionar candidato → geocodificar ---
    const handleSelectCandidate = useCallback(
      async (candidate: AddressCandidate) => {
        setShowDropdown(false);
        setQuery(candidate.address);
        setIsGeocoding(true);
        setErrorMsg(null);

        const result = await geocodeAddress(candidate.address, { municipality });

        setIsGeocoding(false);
        if (result.ok) {
          onAddressGeocoded(result.data);
        } else {
          setErrorMsg(result.error.message);
        }
      },
      [municipality, onAddressGeocoded],
    );

    // --- Limpiar ---
    const handleClear = useCallback(() => {
      setQuery('');
      setCandidates([]);
      setShowDropdown(false);
      setErrorMsg(null);
      inputRef.current?.focus();
    }, []);

    // --- Cerrar dropdown al perder foco (con delay) ---
    const handleBlur = useCallback(() => {
      setTimeout(() => setShowDropdown(false), 200);
    }, []);

    // --- Render ---
    const isLoading = isSearching || isGeocoding;

    return (
      <div className="ptel-address-search relative">
        {/* Input */}
        <div className="relative">
          <Search
            className="absolute left-2 top-1/2 -translate-y-1/2 text-stone-500 pointer-events-none"
            size={14}
          />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={handleInputChange}
            onFocus={() => candidates.length > 0 && setShowDropdown(true)}
            onBlur={handleBlur}
            placeholder={placeholder}
            disabled={disabled || isGeocoding}
            className={`
              w-full pl-7 pr-8 py-1.5 text-xs
              bg-stone-800 border border-stone-600 rounded
              text-stone-200 placeholder:text-stone-500
              focus:outline-none focus:border-amber-500/50
              disabled:opacity-50
            `}
            aria-label="Buscar dirección"
            role="combobox"
            aria-expanded={showDropdown}
            aria-autocomplete="list"
          />

          {/* Spinner / Botón limpiar */}
          {isLoading ? (
            <Loader2
              className="absolute right-2 top-1/2 -translate-y-1/2 text-amber-400 animate-spin"
              size={14}
              data-testid="search-spinner"
            />
          ) : query.length > 0 ? (
            <button
              onClick={handleClear}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-500 hover:text-stone-300"
              aria-label="Limpiar búsqueda"
              type="button"
            >
              <X size={14} />
            </button>
          ) : null}
        </div>

        {/* Dropdown de candidatos */}
        {showDropdown && candidates.length > 0 && (
          <ul
            className="absolute z-50 w-full mt-1 bg-stone-800 border border-stone-600 rounded shadow-lg max-h-48 overflow-y-auto"
            role="listbox"
            aria-label="Candidatos de dirección"
          >
            {candidates.map((candidate, idx) => (
              <li
                key={candidate.id || idx}
                role="option"
                aria-selected={false}
                className="px-3 py-2 text-xs text-stone-300 hover:bg-stone-700 cursor-pointer flex items-start gap-2"
                onMouseDown={() => handleSelectCandidate(candidate)}
              >
                <MapPin className="flex-shrink-0 mt-0.5 text-amber-500" size={12} />
                <span>{candidate.address}</span>
              </li>
            ))}
          </ul>
        )}

        {/* Error */}
        {errorMsg && (
          <p className="mt-1 text-xs text-red-400" role="alert">
            {errorMsg}
          </p>
        )}

        {/* Estado geocodificando */}
        {isGeocoding && (
          <p className="mt-1 text-xs text-amber-400">
            Geocodificando dirección…
          </p>
        )}
      </div>
    );
  },
);

export default AddressSearchInput;
